import React from 'react'

import {Modal, ModalHeader, ModalBody, ModalFooter} from 'Components/Modal';
import {PrimaryButton, DangerButton} from 'Components/Button';
import {Loading, Alert} from 'Components/Partials';
import {Select, Toggle, Input} from 'Components/Form';
import {BooleanFilter, AsyncSelectFilter} from 'Components/Filters';

import AdminReportsApi from 'Services/Api/Admin/Reports';
import AdminScheduledReportReportsApi from 'Services/Api/Admin/ScheduledReportReports';
import GroupsApi from 'Services/Api/Groups/Groups';
import AdminCmsElementApi from 'Services/Api/Admin/Cms/Elements/Element';

import {translationFromJson} from 'Services/TranslationHelpers2';
import {getFieldsFilter} from 'Services/CmsHelpers';

export default class AdminRunReportModal extends React.Component  {
    /**
     * @var state
     */
    state = {
        alert: null,
        working: false,
        availableReports: null,
        type: null,
        params: null,
        displayFilters: false,
        cms_elements: null,
        title: null
    };

    /**
     * @method componentDidUpdate
     */
    async componentDidMount() {
        await this.getAvailableReports();

        if (this.props.type) {
            this.handleTypeChange(this.props.type);
        }
    }

    /**
     * @method getAvailableReports
     */
    getAvailableReports = async () => {
        const response = await AdminReportsApi.getAvailableReports();

        this.setState({
            availableReports: response.data.map((report) => (
                {
                    value: report,
                    label: report.replace(/([A-Z])/g, ' $1').trim()
                }
            )),
        });
    }

    /**
     * @method handleTypeChange
     * @param {string} type
     */
    handleTypeChange = async (type) => {
        this.setState({
            type
        });

        if (type === 'CmsElementRecords' || type === 'CmsElementRecordsForImport') {
            await this.getCmsElements();

            this.setState({
                params: {
                    cms_element_identifier: this.props.cms_element_identifier ?? null
                }
            });
        }
    }

    /**
     * @method getCmsElements
     */
    getCmsElements = async () => {
        // If already loaded then return.
        if (this.state.cms_elements) {
            return;
        }

        this.setState({working: true});

        const response = await AdminCmsElementApi.get(null);

        this.setState({
            working: false,
            cms_elements: response.data.data,
        });
    }

    /**
     * @method getFilters
     * @param {array}
     */
    getFilters = () => {
        const {type, params, cms_elements} = this.state;

        if (type === 'Users') {
            let filters = [
                {
                    column: 'is_admin',
                    label: 'Admin',
                    component: BooleanFilter
                },
                {
                    column: 'enabled',
                    label: 'Enabled',
                    component: BooleanFilter
                }
            ];

            if (window.base.features.groups) {
                filters.push({
                    column: 'group_ids',
                    label: 'Group',
                    component: AsyncSelectFilter,
                    props: {
                        searchCallback: (data) => GroupsApi.get(null, {
                            ...data,
                            hasNoTeam: true
                        }),
                        searchLabelKey: "name"
                    },
                    defaultMethod: false
                });
            }

            return filters;
        } else if (type === 'CmsElementRecords' || type === 'CmsElementRecordsForImport') {
            if (params?.cms_element_identifier) {
                let fields = cms_elements.filter(element => element.identifier === params.cms_element_identifier)[0].fields;

                return getFieldsFilter(fields);
            }
        }

        return null;
    }

    /**
     * @method handleParams
     * @param {string} key
     * @param {string} value
     */
    handleParams = (key, value) => {
        const {params} = this.state;

        this.setState({
            params: {
                ...params,
                [key]: value
            }
        });
    };

    /**
     * @method handleSubmit
     */
    handleSubmit = async () => {
        const {scheduled_report_id, callbackOnSubmit, onClose} = this.props;
        const {type, title} = this.state;

        const params = this.getParamsForSubmission();

        this.setState({
            alert: null,
            working: true
        });

        if (scheduled_report_id) {
            var response = await AdminScheduledReportReportsApi.post(null, {
                scheduled_report_id,
                title,
                type,
                params
            });
        } else {
            var response = await AdminReportsApi.storeReport(
                type,
                params
            );
        }

        if (response.status !== 201) {
            this.setState({
                alert: {
                    type: 'error',
                    message: response.message,
                    errors: response.errors
                },
                working: false
            });
            return;
        }

        if (callbackOnSubmit) {
            callbackOnSubmit(1);
        }

        onClose();
    };

    /**
     * @method getParamsForSubmission
     * @return {object}
     */
    getParamsForSubmission() {
        const {params} = this.state;

        let new_params = {};

        // Convert `username[isEmpty]` to `username` and `isEmpty`.
        if (params) {
            Object.entries(params).map((param) => {
                let key = param[0];
                let value = param[1];

                if (key.includes('[')) {
                    let spliter = key.split('[');

                    key = spliter[0];

                    let key2 = spliter[1].split(']')[0];

                    value = {
                        [key2]: value
                    };
                }

                new_params[key] = value;
            });
        }

        return new_params;
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {scheduled_report_id} = this.props;
        const {alert, working, availableReports, type, title} = this.state;

        return (
            <Modal>
                <ModalBody>
                    <div className="sm:mx-auto sm:w-full sm:max-w-md">
                        <h2 className="my-2 text-center black text-2xl leading-9 font-extrabold">
                            {scheduled_report_id ? "Add Report" : "Run Report"}
                        </h2>

                        {alert !== null && (<Alert {...alert} />)}
                    </div>

                    {scheduled_report_id &&
                        <Input
                            containerClassName="w-72 mt-8 mx-auto"
                            value={title}
                            onChange={(v) => this.setState({'title': v})}
                            placeholder="Enter A Title..."
                        />
                    }

                    <div className="mt-4 mb-60">
                        <Select
                            containerClassName="w-72 mx-auto"
                            placeholder="Select A Report..."
                            value={type}
                            onChange={(v) => this.handleTypeChange(v)}
                            options={availableReports}
                        />
                    </div>

                    {this.renderParams()}
                </ModalBody>

                <ModalFooter alignment="center">
                    {working && (<Loading color="text-black"/>)}

                    {!working &&
                        <div className="flex justify-around space-between">
                            <PrimaryButton className="mr-2" text="Cancel" onClick={this.props.onClose} />
                            <DangerButton onClick={this.handleSubmit} text={scheduled_report_id ? "Add" : "Run"} />
                        </div>
                    }
                </ModalFooter>
            </Modal>
        )
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    renderParams() {
        const {type, params, displayFilters, cms_elements} = this.state;

        let filters = this.getFilters();

        let cms_element_options = cms_elements ? cms_elements.map((element) => (
            {
                label: translationFromJson(element.label),
                value: element.identifier
            }
        )) : null;

        if (filters || type === 'CmsElementRecords' || type === 'CmsElementRecordsForImport') {
            return (
                <div>
                    {(type === 'CmsElementRecords' || type === 'CmsElementRecordsForImport') &&
                        <Select
                            containerClassName="mt-8"
                            label="Cms Element"
                            value={params?.cms_element_identifier}
                            onChange={(v) => this.handleParams('cms_element_identifier', v)}
                            options={cms_element_options}
                        />
                    }

                    {filters &&
                        <Toggle
                            containerClassName={`flex justify-center mt-8 ${displayFilters ? 'mb-4' : ''}`}
                            label="Filter"
                            value={displayFilters}
                            onChange={(v) => this.setState({
                                displayFilters: v,
                                params: {
                                    cms_element_identifier: params?.cms_element_identifier
                                }
                            })}
                        />
                    }

                    {displayFilters && filters && filters.map((filter, i) => {
                        let Filter = filter.component;

                        return (
                            <div className="mb-4 text-black" key={i}>
                                <Filter
                                    containerClassName="w-full"
                                    onChange={(column, value) => this.handleParams(column, value)}
                                    filters={params}
                                    column={filter.column}
                                    props={filter.props}
                                    label={filter.label}
                                    defaultMethod={filter.defaultMethod}
                                />
                            </div>
                        );
                    })}
                </div>
            );
        }

        return null;
    }
}
